/* eslint-disable no-restricted-syntax */
import moment from 'moment';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import { useImperativeHandle, forwardRef, useEffect, useRef, useState } from 'react';
// Google map autocomplete
import Autocomplete from 'react-google-autocomplete';
// form
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { Box, Card, Typography, Checkbox, MenuItem, FormControlLabel, Autocomplete as MUIAutocomplete, TextField, CircularProgress, InputAdornment, Paper, Chip, Stack, Tooltip, Collapse, Button } from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers';
// components
import { Iconify } from '../../../../components';
import { ReferralDropdown } from '../../../../components/Dropdowns';
import { FormProvider, RHFTextField, RHFSelect } from '../../../../components/hook-form';
import RHFToggleGroup from './RHFToggleGroup';
// @store
import { dispatch, useSelector } from '../../../../redux/store';
import { saveNewLeadDetail, updateCompleteStatus, searchLead, clearTaskLead, savePersonalLeadDetails } from '../../../../redux/slices/lead';
import { searchDentallyPatients } from '../../../../utils/dentallyApi';
// validation
import { makePersonalDetailSchema } from '../../../../validations/validations';
// constant
import { inputKeys } from '../../../../constants/LeadDetailConst';

const requiredFieldSx = {
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#00AB55', borderWidth: '1.5px' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#00AB55' },
};

const FIELD_LABELS = {
    firstName: 'First name',
    phoneNumber: 'Phone number',
    title: 'Salutation',
    dob: 'Date of birth',
    sex: 'Gender',
    postcode: 'Postcode',
    email: 'Email',
};

const PersonalDetails = forwardRef(({ handleNext }, ref) => {
    const { enqueueSnackbar } = useSnackbar();
    const { newLeadDetail: { personalDetail }, taskSearchLead, isLoading } = useSelector((state) => state.lead);
    const selectedLead = personalDetail?.existingLeadSelection || null;
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const { dentallyEnabledGlobal, practice: practiceDataPD } = useSelector((state) => state.practice);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchOpen, setSearchOpen] = useState(false);
    const [showAddress, setShowAddress] = useState(false);
    const [dentallyResults, setDentallyResults] = useState([]);
    const [isDentallySearching, setIsDentallySearching] = useState(false);
    const [addressChosen, setAddressChosen] = useState(false);
    
    // Parse free-form search to structured fields
    const parseFreeformInput = (text) => {
        const out = { firstName: '', lastName: '', email: '', phoneNumber: '' };
        if (!text) return out;
        let rest = text.trim();
        // extract email
        const emailMatch = rest.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
        if (emailMatch) {
            out.email = emailMatch[0];
            rest = rest.replace(emailMatch[0], ' ').trim();
        }
        // extract phone (allow +, spaces, dashes)
        const phoneMatch = rest.match(/(\+?\d[\d\s\-().]{6,}\d)/);
        if (phoneMatch) {
            const digits = phoneMatch[0].replace(/[^\d+]/g, '');
            out.phoneNumber = digits;
            rest = rest.replace(phoneMatch[0], ' ').trim();
        }
        // Helper to title-case tokens
        const cleanToken = (s) => s ? s.replace(/[\d_]+/g, '').trim() : '';
        const title = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '';
        const dropWords = new Set(['mr','mrs','ms','miss','dr','sir']);

        // remaining words as name
        let parts = rest.split(/\s+/).filter(Boolean);
        parts = parts.map((p) => cleanToken(p)).filter((p) => p && !dropWords.has(p.toLowerCase()));

        // If no explicit name provided, infer from email local-part
        if (parts.length === 0 && out.email) {
            const local = out.email.split('@')[0];
            // ignore plus addressing
            const base = local.split('+')[0];
            let tokens = base.split(/[._-]+/).filter(Boolean);
            if (tokens.length === 1) {
                // try camelCase split
                tokens = base.split(/(?=[A-Z])/).filter(Boolean);
            }
            tokens = tokens.map((t) => cleanToken(t)).filter(Boolean);
            // remove common role aliases
            tokens = tokens.filter((t) => !dropWords.has(t.toLowerCase()));
            if (tokens.length >= 2) {
                out.firstName = title(tokens[0]);
                out.lastName = title(tokens.slice(1).join(' '));
            } else if (tokens.length === 1) {
                out.firstName = title(tokens[0]);
            }
        }

        if (!out.firstName && parts.length === 1) {
            out.firstName = title(parts[0]);
        } else if (parts.length >= 2) {
            out.firstName = title(parts[0]);
            out.lastName = title(parts.slice(1).join(' '));
        }
        return out;
    };

    // Escape regex special chars
    const escapeRegExp = (s) => s?.replace?.(/[.*+?^${}()|[\]\\]/g, '\\$&') || '';
    // Highlight matches of `query` in `text`
    const highlight = (text, query) => {
        if (!text) return '';
        const q = (query || '').trim();
        if (!q) return text;
        const re = new RegExp(`(${escapeRegExp(q)})`, 'ig');
        const parts = String(text).split(re);
        return parts.map((part, idx) => (
            re.test(part)
                ? <Box component="mark" key={idx} sx={{ bgcolor: 'transparent', color: 'primary.main', fontWeight: 700 }}>{part}</Box>
                : <Box component="span" key={idx}>{part}</Box>
        ));
    };

    const humanize = (val) => (val || '').toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());

    const getContactedCount = (opt) => {
        try {
            const arr = opt?.lead_treatments || [];
            const first = arr[0];
            const cnt = first?.lead_contacted_counts?.[0]?.count;
            return typeof cnt === 'number' ? cnt : null;
        } catch (e) { return null; }
    };

    // eslint-disable-next-line react/prop-types
    const InfoChip = ({ icon, label, color = 'default', title }) => (
        <Tooltip title={title || label} arrow>
            <Chip
                size="small"
                color={color}
                variant="soft"
                icon={<Iconify icon={icon} width={14} height={14} sx={{ mr: 0.5 }} />}
                label={<Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>{label}</Box>}
            />
        </Tooltip>
    );
    const autopopulatingRef = useRef(false);
    const { preferredContactMethodData, referral, sourceType, salutationOptions } = useSelector((state) => state.schema);

    const referralValue = (value, data) => referral?.length > 0 && referral?.filter((res) => res[value] === data.referral);

    // DEV-only: preload test data so the wizard can be exercised without filling every field (Test Practice only)
    const isTestPracticePD = practiceDataPD?.name?.toLowerCase().includes('test practice');
    const devPrefill = import.meta.env.DEV && isTestPracticePD && !personalDetail?.first_name ? {
        firstName: 'Test',
        lastName: 'User',
        title: 'Mr',
        email: 'hello@dentalsem.co.uk',
        phoneNumber: '07852461505',
        dob: new Date('1990-05-15'),
        sex: 'Male',
        postcode: 'SW1A 1AA',
    } : {};

    const defaultValues = {
        firstName: personalDetail && personalDetail?.first_name || devPrefill.firstName || '',
        lastName: personalDetail && personalDetail?.last_name || devPrefill.lastName || '',
        title: personalDetail && personalDetail?.salutation || devPrefill.title || '',
        email: personalDetail && personalDetail?.lead_contact && personalDetail?.lead_contact.email || devPrefill.email || '',
        notes: '',
        // eslint-disable-next-line no-nested-ternary
        phoneNumber: personalDetail && personalDetail?.lead_contact && personalDetail?.lead_contact?.phone || devPrefill.phoneNumber || '',
        dob: personalDetail && personalDetail?.dob && new Date(moment(personalDetail?.dob, 'DD-MM-YYYY')) || devPrefill.dob || null,
        preferredContactMethod: personalDetail?.lead_contact?.preferred_contact_method || 'PHONE',
        address_line_1: personalDetail && personalDetail?.lead_address && personalDetail?.lead_address?.address_line_1 || '',
        county: personalDetail && personalDetail?.lead_address && personalDetail?.lead_address?.county || '',
        city: personalDetail && personalDetail?.lead_address && personalDetail?.lead_address?.city || '',
        postcode: personalDetail && personalDetail?.lead_address && personalDetail?.lead_address?.postcode || devPrefill.postcode || '',
        address_line_2: personalDetail && personalDetail?.lead_address && personalDetail?.lead_address?.address_line_2 || '',
        sex: personalDetail && personalDetail?.sex || devPrefill.sex || '',
        nhs_number: personalDetail?.nhs_number || '',
        emergencyContactName: personalDetail?.emergency_contact_name || '',
        emergencyContactPhone: personalDetail?.emergency_contact_phone || '',
        address: '',
        type: personalDetail && personalDetail?.lead_source && personalDetail?.lead_source?.type || 'TELEPHONE',
        referral: personalDetail && personalDetail?.lead_source && personalDetail?.lead_source?.referral || '',
        isRegister: personalDetail && personalDetail?.is_registered_patient || false
    };
    const methods = useForm({
        resolver: yupResolver(makePersonalDetailSchema(!!dentallyEnabledGlobal)),
        defaultValues,
    });

    const {
        setValue,
        handleSubmit,
        watch,
        control
    } = methods;

    const values = watch();

    // Refs for focusing next inputs
    const firstNameRef = useRef(null);
    const lastNameRef = useRef(null);
    const phoneRef = useRef(null);
    const emailRef = useRef(null);

    const focusNextMissing = (parsed = {}) => {
        try {
            const order = [
                { key: 'firstName', ref: firstNameRef },
                { key: 'lastName', ref: lastNameRef },
                { key: 'phoneNumber', ref: phoneRef },
                { key: 'email', ref: emailRef },
            ];
            // prefer checking parsed (what we just filled), fallback to current form values
            for (const item of order) {
                const hasVal = parsed[item.key] ? true : !!(values?.[item.key]);
                if (!hasVal && item.ref?.current) {
                    // wait for React Hook Form to update value then focus
                    setTimeout(() => {
                        try { item.ref.current?.focus?.(); } catch (e) { /* noop */ }
                    }, 50);
                    break;
                }
            }
        } catch (e) { /* noop */ }
    };

    const onSubmit = async (data, status) => {
        try {
            const detail = {
                salutation: data.title && data.title.toUpperCase() || '',
                sex: data.sex || '',
                first_name: data.firstName,
                dob: data.dob ? moment(data.dob, 'DD/MM/YYYY').format('DD-MM-YYYY') : null,
                is_registered_patient: data.isRegister,
                lead_contact: {
                    phone: data.phoneNumber
                },
                lead_source: {}
            };
            if (data.email) {
                detail.lead_contact.email = data.email;
            }
            if (data.lastName) {
                detail.last_name = data.lastName && data.lastName;
            }
            if (data.notes) {
                detail.lead_contact.note = data.notes;
            }
            if (data.address_line_1) {
                detail.lead_address = {
                    ...detail.lead_address,
                    address_line_1: data.address_line_1
                };
            }
            if (data.postcode) {
                detail.lead_address = {
                    ...detail.lead_address,
                    postcode: data.postcode
                };
            }
            if (data.address_line_2) {
                detail.lead_address = {
                    ...detail.lead_address,
                    address_line_2: data.address_line_2
                };
            }
            if (data.city) {
                detail.lead_address = {
                    ...detail.lead_address,
                    city: data.city,
                };
            }
            if (data.county) {
                detail.lead_address = {
                    ...detail.lead_address,
                    county: data.county
                };
            }
            if (data.preferredContactMethod) {
                detail.lead_contact.preferred_contact_method = data.preferredContactMethod.toUpperCase();
            }
            if (data.type) {
                detail.lead_source = {
                    ...detail.lead_source,
                    type: data.type
                };
            }
            if (data.referral) {
                detail.lead_source = {
                    ...detail.lead_source,
                    referral: data.referral
                };
            }
            if (data.affiliate_user) {
                detail.lead_source = {
                    ...detail.lead_source,
                    affiliate_user: data.affiliate_user,
                    type: "CLINICIAN"
                };
            }
            if (data.nhs_number) { detail.nhs_number = data.nhs_number; }
            if (data.emergencyContactName) { detail.emergency_contact_name = data.emergencyContactName; }
            if (data.emergencyContactPhone) { detail.emergency_contact_phone = data.emergencyContactPhone; }
            dispatch(saveNewLeadDetail({ personalDetail: detail, personalDetailEdited: true }));
            if (status?.toLowerCase?.() === 'finish') {
                dispatch(updateCompleteStatus(true));
                // Also call handleNext to notify parent to close the popup
                handleNext(true);
                return;
            }
            handleNext(true);
        }
        catch (err) {
            console.error("Error in personal details", err);
        }
    };

    const setAddressData = (addresses, fulladdress) => {
        ClearAddressData();
        if (addresses && addresses.length > 0) {
            const address1 = fulladdress.split(',')[0];
            setValue('address_line_1', address1);
            addresses.forEach(address => {
                if (address.types.indexOf('postal_code') > -1) {
                    setValue('postcode', address.long_name);
                }
                if (address.types.indexOf('country') > -1) {
                    if (address1 === address.long_name) {
                        setValue('address_line_1', '');
                    }
                    setValue('county', address.long_name);
                }
                if (address.types.indexOf('locality') > -1 || address.types.indexOf('postal_town') > -1) {
                    if (address1.includes(address.long_name)) {
                        setValue('address_line_1', '');
                    }
                    setValue('city', address.long_name);
                }
                if (address.types.indexOf('route') > -1) {
                    if (!address1.includes(address.long_name)) {
                        setValue('address_line_2', address.long_name);
                    } else {
                        setValue('address_line_2', '');
                    }
                }
            });
            setAddressChosen(true);
        } else {
            ClearAddressData();
        }
    };

    const ClearAddressData = () => {
        setValue('postcode', '');
        setValue('address_line_2', '');
        setValue('county', '');
        setValue('city', '');
        setValue('address_line_1', '');
        setAddressChosen(false);
    };
    useImperativeHandle(
        ref, () => ({
            async submit(status) {
                await handleSubmit((data) => {
                    onSubmit(data, status);
                }, (errors) => {
                    const missing = Object.keys(errors)
                        .map((k) => FIELD_LABELS[k] || k)
                        .join(', ');
                    enqueueSnackbar(`Please complete required fields: ${missing}`, {
                        variant: 'error',
                        anchorOrigin: { vertical: 'top', horizontal: 'center' },
                    });
                })();
            },
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [handleSubmit]
    );

    const handleChange = (event, data) => {
        const {
            target: { value },
        } = event;

        setValue(data, value);
    };

    const handleReferral = (event, data) => {
        if (data) {
            setValue('referral', data?.id ? "CLINICIAN" : data?.value);
            if (data?.id) {
                setValue('affiliate_user', data?.id);
                setValue('type', "CLINICIAN");
            }
        }
    };

    const getAddressField = () => {
        if (values.address_line_1 || values.address_line_2 || values.city || values.county || values.postcode) {
            return true;
        }
        return false;
    };

    const numberKeyDownFunc = (evt) => {
        const isControlC = (evt.ctrlKey && evt.key === 'c') || (evt.metaKey && evt.key === 'c');
        const isControlV = (evt.ctrlKey && evt.key === 'v') || (evt.metaKey && evt.key === 'v');
        const isAllowedKey = inputKeys.includes(evt.key) || (/^[0-9]*$/).test(evt.key);
        if (!(isControlC || isControlV || isAllowedKey)) {
            evt.preventDefault();
        }
    };

    // Keep redux in sync with phone edits without overriding RHF onChange
    useEffect(() => {
        if (values?.phoneNumber !== undefined) {
            dispatch(savePersonalLeadDetails({ phoneNumber: values.phoneNumber, lead_contact: { phone: values.phoneNumber } }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values?.phoneNumber]);

    // Auto-expand address section if address data exists or Dentally is enabled (postcode required)
    useEffect(() => {
        if (getAddressField() || dentallyEnabledGlobal) {
            setShowAddress(true);
        }
        if (getAddressField()) {
            setAddressChosen(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Clear selected lead on any user edits to personal fields (beyond autopopulate)
    useEffect(() => {
        if (!selectedLead || autopopulatingRef.current) return;
        const changed = (
            (values.firstName || '') !== (selectedLead?.first_name || '') ||
            (values.lastName || '') !== (selectedLead?.last_name || '') ||
            (values.phoneNumber || '') !== (selectedLead?.lead_contact?.phone || '') ||
            (values.email || '') !== (selectedLead?.lead_contact?.email || '') ||
            (values.dob ? moment(values.dob).format('DD-MM-YYYY') : '') !== (selectedLead?.dob || '')
        );
        if (changed) {
            dispatch(savePersonalLeadDetails({ existingLeadSelection: null }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.firstName, values.lastName, values.phoneNumber, values.email, values.dob, selectedLead]);

    // Debounce existing lead search + parallel Dentally patient search
    useEffect(() => {
        if (!practiceId) return undefined;
        if (!searchQuery || searchQuery.trim().length < 2) {
            setDentallyResults([]);
            return undefined;
        }
        const handler = setTimeout(() => {
            dispatch(searchLead(encodeURIComponent(searchQuery.trim()), practiceId, 'taskLead'));
            if (dentallyEnabledGlobal) {
                setIsDentallySearching(true);
                searchDentallyPatients(practiceId, searchQuery.trim())
                    .then((res) => {
                        const d = res?.data;
                        let patients = [];
                        if (Array.isArray(d?.patients)) patients = d.patients;
                        else if (Array.isArray(d?.results)) patients = d.results;
                        else if (Array.isArray(d)) patients = d;
                        else if (Array.isArray(d?.data)) patients = d.data;
                        setDentallyResults(patients.map((p) => ({ ...p, __dentally__: true })));
                    })
                    .catch(() => setDentallyResults([]))
                    .finally(() => setIsDentallySearching(false));
            }
        }, 300);
        return () => clearTimeout(handler);
    }, [searchQuery, practiceId, dentallyEnabledGlobal]);

    return (
        <FormProvider methods={methods}>
                    <Card sx={{ p: 3, boxShadow: 'none' }}>
                        {/* Global, full-width lead search */}
                        <Box sx={{ mb: 3 }}>
                            <MUIAutocomplete
                                freeSolo
                                fullWidth
                                openOnFocus
                                open={searchOpen}
                                onOpen={() => setSearchOpen(true)}
                                onClose={() => setSearchOpen(false)}
                                // Build options: CRM leads + Dentally patients + sticky "Create" footer
                                options={(() => {
                                    const crmBase = (taskSearchLead || []);
                                    const q = (searchQuery || '').trim();
                                    if (!q) return crmBase;
                                    const parsed = parseFreeformInput(q);
                                    const namePart = [parsed.firstName, parsed.lastName].filter(Boolean).join(' ').trim();
                                    const parts = [];
                                    if (namePart) parts.push(`name "${namePart}"`);
                                    if (parsed.email) parts.push(`email "${parsed.email}"`);
                                    if (parsed.phoneNumber) parts.push(`phone "${parsed.phoneNumber}"`);
                                    const createLabel = parts.length ? `Create lead with ${parts.join(' · ')}` : `Create new lead from "${q}"`;
                                    const createOption = { id: '__create__', __create__: true, label: createLabel, parsed };
                                    return [...crmBase, ...dentallyResults, createOption];
                                })()}
                                filterOptions={(x) => x}
                                groupBy={(option) => {
                                    if (option.__create__) return '';
                                    if (option.__dentally__) return 'Dentally Patients';
                                    return 'CRM Leads';
                                }}
                                getOptionLabel={(option) => {
                                    if (option?.__create__) return option?.label;
                                    if (option?.__dentally__) return [option.first_name, option.last_name].filter(Boolean).join(' ') || option.name || `Patient #${option.id}`;
                                    return `${option?.first_name || ''} ${option?.last_name || ''}`.trim();
                                }}
                                isOptionEqualToValue={(option, value) => {
                                    if (option?.__dentally__ && value?.__dentally__) return String(option.id) === String(value.id);
                                    return (option?.id || option) === (value?.id || value);
                                }}
                                loading={(Boolean(isLoading) && Boolean(searchQuery?.trim()?.length >= 2)) || isDentallySearching}
                                inputValue={searchQuery}
                                onInputChange={(e, value, reason) => {
                                    if (reason === 'input') {
                                        setSearchQuery(value);
                                        setSearchOpen(Boolean((value || '').trim().length > 0));
                                        if (!value || value.trim().length < 2) {
                                            dispatch(clearTaskLead());
                                            setDentallyResults([]);
                                        }
                                    }
                                    if (reason === 'clear') {
                                        setSearchQuery('');
                                        setSearchOpen(false);
                                        dispatch(clearTaskLead());
                                        setDentallyResults([]);
                                    }
                                }}
                                onChange={(e, val) => {
                                    if (!val) return;
                                    // Handle freeSolo enter: val can be string
                                    if (typeof val === 'string') {
                                        const parsed = parseFreeformInput(val.trim());
                                        if (parsed.firstName) setValue('firstName', parsed.firstName);
                                        if (parsed.lastName) setValue('lastName', parsed.lastName);
                                        if (parsed.email) setValue('email', parsed.email);
                                        if (parsed.phoneNumber) setValue('phoneNumber', parsed.phoneNumber);
                                        dispatch(savePersonalLeadDetails({ existingLeadSelection: null }));
                                        setSearchOpen(false);
                                        dispatch(clearTaskLead());
                                        focusNextMissing(parsed);
                                        return;
                                    }
                                    // Handle create option
                                    if (val?.__create__) {
                                        const parsed = val?.parsed || parseFreeformInput((searchQuery || '').trim());
                                        if (parsed.firstName) setValue('firstName', parsed.firstName);
                                        if (parsed.lastName) setValue('lastName', parsed.lastName);
                                        if (parsed.email) setValue('email', parsed.email);
                                        if (parsed.phoneNumber) setValue('phoneNumber', parsed.phoneNumber);
                                        // ensure we're in new-lead mode (no selected existing lead)
                                        dispatch(savePersonalLeadDetails({ existingLeadSelection: null }));
                                        setSearchOpen(false);
                                        dispatch(clearTaskLead());
                                        focusNextMissing(parsed);
                                        return;
                                    }
                                    // Populate from selected Dentally patient
                                    if (val?.__dentally__) {
                                        const patient = val;
                                        autopopulatingRef.current = true;
                                        if (patient.first_name) setValue('firstName', patient.first_name);
                                        if (patient.last_name) setValue('lastName', patient.last_name);
                                        const title = (patient.title || '').toUpperCase();
                                        if (title) setValue('title', title);
                                        const email = patient.email_address || patient.email || '';
                                        if (email) setValue('email', email);
                                        const phone = patient.mobile_phone || patient.phone_number || patient.mobile || '';
                                        if (phone) setValue('phoneNumber', phone);
                                        if (patient.date_of_birth) {
                                            const parsedDob = moment(patient.date_of_birth, ['YYYY-MM-DD', 'DD-MM-YYYY']);
                                            if (parsedDob.isValid()) setValue('dob', parsedDob.toDate());
                                        }
                                        const rawSex = patient.gender || patient.sex || '';
                                        if (rawSex) {
                                            const sexNorm = rawSex.charAt(0).toUpperCase() + rawSex.slice(1).toLowerCase();
                                            if (['Male', 'Female', 'Unknown'].includes(sexNorm)) setValue('sex', sexNorm);
                                        }
                                        if (patient.address_line_1) setValue('address_line_1', patient.address_line_1);
                                        if (patient.postcode || patient.postal_code) {
                                            setValue('postcode', patient.postcode || patient.postal_code || '');
                                            setAddressChosen(true);
                                        }
                                        dispatch(savePersonalLeadDetails({ existingLeadSelection: null }));
                                        setTimeout(() => { autopopulatingRef.current = false; }, 0);
                                        setSearchOpen(false);
                                        dispatch(clearTaskLead());
                                        setDentallyResults([]);
                                        return;
                                    }
                                    // Populate all details from selected existing CRM lead
                                    const lead = val;
                                    autopopulatingRef.current = true;
                                    setValue('firstName', lead?.first_name || '');
                                    setValue('lastName', lead?.last_name || '');
                                    setValue('title', (lead?.salutation || '').toUpperCase());
                                    setValue('email', lead?.lead_contact?.email || '');
                                    setValue('phoneNumber', lead?.lead_contact?.phone || '');
                                    if (lead?.dob) {
                                        const parsedDob = moment(lead?.dob, 'DD-MM-YYYY');
                                        if (parsedDob.isValid()) setValue('dob', parsedDob.toDate());
                                    }
                                    if (lead?.lead_address) {
                                        setValue('address_line_1', lead?.lead_address?.address_line_1 || '');
                                        setValue('address_line_2', lead?.lead_address?.address_line_2 || '');
                                        setValue('city', lead?.lead_address?.city || '');
                                        setValue('county', lead?.lead_address?.county || '');
                                        setValue('postcode', lead?.lead_address?.postcode || '');
                                    }
                                    if (lead?.lead_contact?.preferred_contact_method) {
                                        setValue('preferredContactMethod', lead?.lead_contact?.preferred_contact_method || '');
                                    }
                                    if (lead?.lead_source) {
                                        setValue('type', lead?.lead_source?.type || '');
                                        setValue('referral', lead?.lead_source?.referral || '');
                                    }
                                    dispatch(savePersonalLeadDetails({
                                        existingLeadSelection: lead,
                                        first_name: lead?.first_name || '',
                                        firstName: lead?.first_name || '',
                                        last_name: lead?.last_name || '',
                                        salutation: lead?.salutation || '',
                                        phoneNumber: lead?.lead_contact?.phone || '',
                                        lead_contact: {
                                            email: lead?.lead_contact?.email || '',
                                            phone: lead?.lead_contact?.phone || '',
                                            preferred_contact_method: lead?.lead_contact?.preferred_contact_method || ''
                                        },
                                        lead_address: lead?.lead_address || {},
                                        lead_source: lead?.lead_source || {},
                                    }));
                                    setTimeout(() => { autopopulatingRef.current = false; }, 0);
                                    setSearchOpen(false);
                                    dispatch(clearTaskLead());
                                    setDentallyResults([]);
                                }}
                                renderGroup={(params) => (
                                    <li key={params.key}>
                                        {params.group && (
                                            <Box sx={{ px: 1.5, py: 0.75, display: 'flex', alignItems: 'center', gap: 0.75, bgcolor: 'rgba(145,158,171,0.08)', borderBottom: '1px solid rgba(145,158,171,0.12)' }}>
                                                {params.group === 'Dentally Patients' && (
                                                    <Iconify icon='mdi:tooth-outline' width={13} sx={{ color: 'primary.main' }} />
                                                )}
                                                <Typography variant='caption' sx={{ fontWeight: 700, fontSize: 10, letterSpacing: '0.5px', textTransform: 'uppercase', color: params.group === 'Dentally Patients' ? 'primary.main' : 'text.secondary' }}>
                                                    {params.group}
                                                </Typography>
                                            </Box>
                                        )}
                                        <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder={dentallyEnabledGlobal ? "Search existing leads or Dentally patients" : "Search existing leads"}
                                        label="Search Leads"
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Iconify icon='eva:search-outline' color='grey.600' width={20} height={20} />
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <>
                                                    {Boolean(isLoading) && Boolean(searchQuery?.trim()?.length >= 2) ? (
                                                        <InputAdornment position="end">
                                                            <CircularProgress color="inherit" size={18} />
                                                        </InputAdornment>
                                                    ) : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                                renderOption={(props, option) => {
                                    if (option?.__create__) {
                                        return (
                                            <li
                                                {...props}
                                                key={option?.id}
                                                style={{
                                                    position: 'sticky',
                                                    bottom: 0,
                                                    background: '#fff',
                                                    borderTop: '1px solid rgba(145, 158, 171, 0.16)',
                                                    zIndex: 2,
                                                    margin: 0,
                                                    padding: 0,
                                                    borderRadius: 0,
                                                    display: 'flex',
                                                    alignItems: 'center'
                                                }}
                                            >
                                                <Box sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns: '28px 1fr auto',
                                                    alignItems: 'center',
                                                    columnGap: 1,
                                                    rowGap: 0.5,
                                                    px: 1.5,
                                                    py: 1,
                                                    width: '100%',
                                                    minHeight: 56,
                                                    backgroundColor: '#fff',
                                                    boxShadow: '0 -6px 12px rgba(145,158,171,0.08)'
                                                }}>
                                                    <Iconify icon='eva:plus-circle-outline' width={22} height={22} color='primary.main' />
                                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>Create lead</Typography>
                                                        <Stack direction="row" spacing={0.75} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                                                            {(() => {
                                                                const p = option?.parsed || {};
                                                                const fullName = [p.firstName, p.lastName].filter(Boolean).join(' ');
                                                                return fullName ? (
                                                                    <Chip size='small' variant='soft' color='info' icon={<Iconify icon='mdi:account-outline' width={14} height={14} />} label={`Name: ${fullName}`} />
                                                                ) : null;
                                                            })()}
                                                            {option?.parsed?.email && (
                                                                <Chip size='small' variant='soft' color='success' icon={<Iconify icon='mdi:email-outline' width={14} height={14} />} label={`Email: ${option?.parsed?.email}`} />
                                                            )}
                                                            {option?.parsed?.phoneNumber && (
                                                                <Chip size='small' variant='soft' color='secondary' icon={<Iconify icon='mdi:phone-outline' width={14} height={14} />} label={`Phone: ${option?.parsed?.phoneNumber}`} />
                                                            )}
                                                        </Stack>
                                                    </Box>
                                                    <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap', alignSelf: 'start' }}>Enter</Typography>
                                                </Box>
                                            </li>
                                        );
                                    }
                                    if (option?.__dentally__) {
                                        const dName = [option.first_name, option.last_name].filter(Boolean).join(' ') || option.name || `Patient #${option.id}`;
                                        const dEmail = option.email_address || option.email || '';
                                        const dPhone = option.mobile_phone || option.phone_number || option.mobile || '';
                                        const dDob = option.date_of_birth ? `DOB: ${option.date_of_birth}` : '';
                                        return (
                                            <li
                                                {...props}
                                                key={`dentally-${option.id}`}
                                                style={{ padding: 0, margin: 0, display: 'flex', alignItems: 'center', minHeight: 52 }}
                                            >
                                                <Box sx={{ display: 'flex', flexDirection: 'column', px: 1.5, py: 1, width: '100%' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                        <Iconify icon='mdi:tooth-outline' width={15} sx={{ color: 'primary.main', flexShrink: 0 }} />
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>
                                                            {highlight(dName, searchQuery)}
                                                        </Typography>
                                                        <Chip label="Dentally" size="small" color="primary" sx={{ height: 18, fontSize: 10, fontWeight: 700, letterSpacing: '0.3px', borderRadius: '4px' }} />
                                                    </Box>
                                                    {(dPhone || dEmail || dDob) && (
                                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, pl: 2.75 }}>
                                                            {[dPhone, dEmail, dDob].filter(Boolean).join(' · ')}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </li>
                                        );
                                    }
                                    return (
                                        <li
                                            {...props}
                                            key={option?.id}
                                            style={{ padding: 0, margin: 0, display: 'flex', alignItems: 'center', minHeight: 56 }}
                                        >
                                            <Box sx={{ display: 'flex', flexDirection: 'column', px: 1.5, py: 1, width: '100%' }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                    {highlight(`${option?.first_name || ''} ${option?.last_name || ''}`.trim(), searchQuery)}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25 }}>
                                                    <Box component="span">{highlight(option?.lead_contact?.phone || '', searchQuery)}</Box>
                                                    {option?.lead_contact?.email ? <Box component="span"> {` · `}{highlight(option?.lead_contact?.email, searchQuery)}</Box> : null}
                                                </Typography>
                                                <Stack direction="row" spacing={0.75} sx={{ mt: 0.75, flexWrap: 'wrap' }}>
                                                    {option?.lead_source?.type && (
                                                        <InfoChip icon='mdi:web' color='info' label={`Source: ${humanize(option?.lead_source?.type)}`} title='Lead Source' />
                                                    )}
                                                    {option?.lead_source?.referral && (
                                                        <InfoChip icon='mdi:tag-outline' color='success' label={`Referral: ${humanize(option?.lead_source?.referral)}`} title='Referral' />
                                                    )}
                                                    {option?.healthcare_provider && (
                                                        <InfoChip icon='mdi:hospital-building' color='secondary' label={`Provider: ${humanize(option?.healthcare_provider)}`} title='Healthcare Provider' />
                                                    )}
                                                    {(() => {
                                                        const t = option?.lead_treatments?.[0]?.treatment;
                                                        return t ? <InfoChip icon='mdi:tooth-outline' color='default' label={`Treatment: ${t}`} title='Treatment' /> : null;
                                                    })()}
                                                    {(() => {
                                                        const cnt = getContactedCount(option);
                                                        return (cnt !== null) ? (
                                                            <InfoChip icon='mdi:phone-clock' color='warning' label={`Contacted: ${cnt}`} title='Times contacted' />
                                                        ) : null;
                                                    })()}
                                                </Stack>
                                            </Box>
                                        </li>
                                    );
                                }}
                                ListboxProps={{ style: { maxHeight: 360, position: 'relative', padding: 0, margin: 0, overflowY: 'auto', overscrollBehavior: 'contain', background: '#fff' } }}
                                noOptionsText={searchQuery?.trim()?.length >= 2 ? 'No matching leads or Dentally patients' : 'Type at least 2 characters'}
                                PaperComponent={(paperProps) => {
                                    const { children, ...rest } = paperProps;
                                    return (
                                        <Paper
                                            {...rest}
                                            elevation={8}
                                            sx={{
                                                borderRadius: 1.5,
                                                overflow: 'hidden',
                                                border: '1px solid rgba(145, 158, 171, 0.24)',
                                                boxShadow: '0 8px 24px rgba(145,158,171,0.24)',
                                                m: 0,
                                                position: 'relative',
                                                bgcolor: '#fff'
                                            }}
                                        >
                                            {children}
                                        </Paper>
                                    );
                                }}
                            />
                            {/* The sticky create option in the dropdown replaces the external button */}
                        </Box>
                        {/* Contact Information Section */}
                        <Typography variant="overline" sx={{ color: 'text.secondary', mt: 2, mb: 1, display: 'block' }}>
                            Contact Information
                        </Typography>
                        <Box
                            sx={{
                                display: 'grid',
                                columnGap: 2,
                                rowGap: 1.5,
                                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
                                mb: 2,
                            }}
                        >
                            <RHFTextField name="firstName" label="First name (Required)" inputRef={firstNameRef} sx={requiredFieldSx} />
                            <RHFTextField name="lastName" label="Last name" inputRef={lastNameRef} />
                            <RHFTextField
                                name='phoneNumber'
                                type='string'
                                inputProps={{ maxLength: 15 }}
                                label='Phone number (Required)'
                                onKeyDown={(evt) => numberKeyDownFunc(evt)}
                                inputRef={phoneRef}
                                sx={requiredFieldSx}
                            />
                            <RHFTextField name='email' label='Email' inputRef={emailRef} />
                            {dentallyEnabledGlobal && (
                            <RHFSelect
                                name={'title'}
                                label='Title (Required)'
                                InputLabelProps={{ shrink: true }}
                                SelectProps={{ native: false, sx: { textTransform: 'capitalize' } }}
                                sx={requiredFieldSx}
                            >
                                {salutationOptions?.map((option, index) => (
                                    <MenuItem
                                        key={index}
                                        value={option.value}
                                        sx={{
                                            mx: 1,
                                            my: 0.5,
                                            borderRadius: 0.75,
                                            typography: 'body2',
                                            textTransform: 'capitalize',
                                        }}
                                    >
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </RHFSelect>
                            )}
                            {dentallyEnabledGlobal && (
                            <Box>
                                <Controller
                                    name="dob"
                                    control={control}
                                    render={({ field, fieldState: { error } }) => (
                                        <DesktopDatePicker
                                            orientation="landscape"
                                            label='Date of birth (Required)'
                                            value={field.value || null}
                                            format="dd/MM/yyyy"
                                            maxDate={new Date()}
                                            onChange={(newValue) => { field.onChange(newValue); }}
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true,
                                                    error: !!error,
                                                    helperText: error?.message,
                                                    sx: !error ? requiredFieldSx : undefined,
                                                },
                                            }}
                                        />
                                    )}
                                />
                            </Box>
                            )}
                            <RHFSelect
                                name="sex"
                                label="Gender"
                                SelectProps={{ native: false, sx: { textTransform: 'capitalize' } }}
                            >
                                {['Male', 'Female', 'Unknown'].map((g) => (
                                    <MenuItem key={g} value={g} sx={{ mx: 1, my: 0.5, borderRadius: 0.75, typography: 'body2', textTransform: 'capitalize' }}>
                                        {g}
                                    </MenuItem>
                                ))}
                            </RHFSelect>
                            <FormControlLabel
                                control={
                                    <Controller
                                        control={control}
                                        name={'isRegister'}
                                        render={({ field: regField }) => (
                                            <Checkbox
                                                areaLabel='register-checkbox'
                                                onChange={e => regField.onChange(e.target.checked)}
                                                checked={regField.value}
                                                size='small'
                                            />
                                        )}
                                    />
                                }
                                label={'Is registered patient'}
                                sx={{ alignSelf: 'center' }}
                            />
                        </Box>

                        {/* Address Section */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, mb: 1 }}>
                            <Typography variant="overline" sx={{ color: dentallyEnabledGlobal ? 'primary.main' : 'text.secondary' }}>
                                Address{dentallyEnabledGlobal ? ' (Postcode Required)' : ''}
                            </Typography>
                            {!showAddress && (
                                <Button variant="text" size="small" onClick={() => setShowAddress(true)} sx={{ textTransform: 'none', fontSize: 12 }}>
                                    + Add address
                                </Button>
                            )}
                            {showAddress && addressChosen && (
                                <Button variant="text" size="small" onClick={ClearAddressData} sx={{ textTransform: 'none', fontSize: 12, color: 'text.secondary' }}>
                                    Clear address
                                </Button>
                            )}
                        </Box>
                        <Collapse in={showAddress} mountOnEnter>
                            {/* Search bar — always visible */}
                            <Controller
                                name="address"
                                control={control}
                                render={({ field }) => (
                                    <Box sx={{ position: 'relative', mb: 1.5 }}>
                                        <Iconify icon='eva:search-outline' color='grey.500' width={20} height={20} sx={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                                        <Autocomplete
                                            apiKey='AIzaSyD_7FXxCWgBUUd6922n33WKqrfRDsXXxj8'
                                            onPlaceSelected={(place, inputRef) => {
                                                setAddressData(place.address_components, inputRef.value);
                                                field.onChange(inputRef.value);
                                            }}
                                            style={{ paddingLeft: '40px', fontSize: '0.875rem', width: '100%', boxSizing: 'border-box' }}
                                            options={{
                                                types: ['establishment', 'geocode'],
                                                fields: ['address_components', 'formatted_address'],
                                                componentRestrictions: { country: 'uk' },
                                            }}
                                            defaultValue={field.value}
                                            className='address-input'
                                            placeholder='Search address or postcode...'
                                        />
                                    </Box>
                                )}
                            />
                            {/* Address fields — shown only after an address is selected */}
                            {addressChosen && (
                                <Box
                                    sx={{
                                        display: 'grid',
                                        columnGap: 2,
                                        rowGap: 1.5,
                                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                                        mb: 2,
                                    }}
                                >
                                    <RHFTextField name="address_line_1" label="Address line 1" />
                                    <RHFTextField name="address_line_2" label="Address line 2" />
                                    <RHFTextField name="city" label="City" />
                                    <RHFTextField name="county" label="County" />
                                    <RHFTextField name="postcode" label={dentallyEnabledGlobal ? "Postcode (Required)" : "Postcode"} sx={dentallyEnabledGlobal ? requiredFieldSx : undefined} />
                                </Box>
                            )}
                        </Collapse>

                        {/* Medical & Emergency */}
                        <Typography variant="overline" sx={{ color: 'text.secondary', mt: 2, mb: 1, display: 'block' }}>
                            Medical & Emergency
                        </Typography>
                        <Box
                            sx={{
                                display: 'grid',
                                columnGap: 2,
                                rowGap: 1.5,
                                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                                alignItems: 'start',
                                mb: 2,
                            }}
                        >
                            <RHFTextField name="nhs_number" label="NHS Number" />
                            <RHFTextField name="emergencyContactName" label="Emergency Contact Name" />
                            <RHFTextField
                                name="emergencyContactPhone"
                                label="Emergency Contact Phone"
                                type="string"
                                inputProps={{ maxLength: 15 }}
                                onKeyDown={(evt) => {
                                    const isControlC = (evt.ctrlKey && evt.key === 'c') || (evt.metaKey && evt.key === 'c');
                                    const isControlV = (evt.ctrlKey && evt.key === 'v') || (evt.metaKey && evt.key === 'v');
                                    const isAllowedKey = inputKeys.includes(evt.key) || (/^[0-9]*$/).test(evt.key);
                                    if (!(isControlC || isControlV || isAllowedKey)) {
                                        evt.preventDefault();
                                    }
                                }}
                            />
                        </Box>

                        {/* Lead Source & Preferences */}
                        <Typography variant="overline" sx={{ color: 'text.secondary', mt: 2, mb: 1, display: 'block' }}>
                            Lead Source & Preferences
                        </Typography>
                        <Box
                            sx={{
                                display: 'grid',
                                columnGap: 2,
                                rowGap: 1.5,
                                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                                alignItems: 'start',
                                mb: 2,
                            }}
                        >
                            <RHFSelect
                                name='type'
                                label='Source'
                                areaLabel='leadType'
                                value={values.type}
                                SelectProps={{ native: false, sx: { textTransform: 'capitalize' } }}
                            >
                                {sourceType?.map((options, index) => (
                                    <MenuItem
                                        key={index}
                                        value={options.value}
                                        onChange={(e) => handleChange(e, 'type')}
                                        sx={{
                                            mx: 1,
                                            my: 0.5,
                                            borderRadius: 0.75,
                                            typography: 'body2',
                                            textTransform: 'capitalize',
                                        }}
                                    >
                                        {options.label}
                                    </MenuItem>
                                ))}
                            </RHFSelect>
                            <ReferralDropdown
                                name={'referral'}
                                areaLabel='leadReferral'
                                value={referralValue('value', values)[0]?.label || ''}
                                onChange={handleReferral}
                                affiliateUser={values?.affiliate_user ? { id: values?.affiliate_user } : personalDetail?.lead_source?.affiliate_user}
                            />
                            <RHFToggleGroup
                                control={control}
                                name='preferredContactMethod'
                                label='Preferred Contact'
                                options={preferredContactMethodData}
                            />
                        </Box>
                    </Card>
        </FormProvider>
    );
});

PersonalDetails.propTypes = {
    handleNext: PropTypes.func,
    type: PropTypes.string,
};

export default PersonalDetails;
